// ProgrammeComplete.tsx — MAINTENANCE MODE. Shown on Today once every step of the
// whole programme is done. It is the ongoing home for a graduate: a warm close +
// Craig's image + a ROTATING Act-3 / completion quote (changes day to day so it stays
// alive), then the living parts of maintenance as real buttons — the three maintenance
// tools (moment-to-moment, daily, monthly), Regulate now, the recovery dashboard, and
// the Journey to revisit any chapter. Recovery is non-linear: this is a place to keep
// turning up, not an end. The self-compassion completion adds a "roll the dice" button.
import { Display, Body, Btn } from '@/components/ui';
import { Icon } from '@/components/icons';
import { clipUrl, GUIDE_MESSAGES } from '@/data/guideMessages';

// Pool of Craig's own quotes for the maintenance screen: the Act-3 act-boundary
// quotes + the mastery/completion quotes. Read defensively so a shape change never
// throws — falls back to the fixed line if the pool is somehow empty.
const MAINT_QUOTES: string[] = (() => {
  const out: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const h4 = GUIDE_MESSAGES.H4_actBoundary as any;
  const act3 = h4?.quotePoolByAct?.act3 ?? h4?.act3 ?? [];
  for (const q of act3) if (q?.text) out.push(q.text as string);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mastery = (GUIDE_MESSAGES.H5_mastery as any)?.quotePool ?? [];
  for (const q of mastery) if (q?.text) out.push(q.text as string);
  return out;
})();
const FALLBACK_QUOTE =
  'What you’ve done is a journey towards empowerment and self-love — towards becoming someone who can meet their own needs. Be proud of that. I am.';
function todaysQuote(): string {
  if (!MAINT_QUOTES.length) return FALLBACK_QUOTE;
  const day = Math.floor(Date.now() / 86_400_000); // rotates once per calendar day
  return MAINT_QUOTES[day % MAINT_QUOTES.length];
}

export function ProgrammeComplete({
  onRegulate,
  onJourney,
  onRollDice,
  onOpenTool,
  onOpenDashboard,
  onOpenPlan,
  monthlyDue = false,
  isCompassion = false,
}: {
  onRegulate: () => void;
  /** Open the Journey page to revisit any chapter. */
  onJourney?: () => void;
  /** Self-compassion only: roll the compassion dice. */
  onRollDice?: () => void;
  /** Open a maintenance toolkit exercise by id (maint-moment / maint-daily / maint-monthly). */
  onOpenTool?: (toolId: string) => void;
  /** Open the recovery dashboard (check-in pulse + graphs). */
  onOpenDashboard?: () => void;
  /** Open the "Self-Love & Maintenance Plan" quick-reference lesson (act3-c12-l06). */
  onOpenPlan?: () => void;
  /** Gentle "Due" cue on the monthly check-in when ~a month has passed. */
  monthlyDue?: boolean;
  isCompassion?: boolean;
}) {
  const quote = todaysQuote();
  return (
    <div style={{ textAlign: 'center', padding: '20px 8px 8px', position: 'relative' }}>
      <img
        src={clipUrl('maintaining')}
        alt=""
        aria-hidden="true"
        style={{ display: 'block', width: '100%', maxHeight: 200, objectFit: 'cover', objectPosition: 'center', borderRadius: 16, marginBottom: 12 }}
      />

      <div
        style={{
          fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: 'var(--amber-deep)', marginBottom: 12,
        }}
      >
        {isCompassion ? 'Self-compassion complete' : 'Maintenance mode'}
      </div>

      <Display parts={{ pre: 'You did the ', em: 'work', post: '.' }} size={27} lh={1.18} />

      {/* Rotating Act-3 / completion quote — keeps the screen fresh day to day. */}
      <p
        style={{
          margin: '16px auto 0', maxWidth: 332, fontFamily: 'Fraunces, Georgia, serif', fontStyle: 'italic',
          fontWeight: 300, fontSize: 18, lineHeight: 1.55, color: 'var(--amber-deep)',
        }}
      >
        “{quote}”
      </p>

      <Body size={15} style={{ margin: '16px auto 0', maxWidth: 340, lineHeight: 1.6 }}>
        The work doesn’t stop here — it just becomes part of how you live. Keep turning up for yourself:
      </Body>

      {/* Maintenance hub — the living practices as real buttons. */}
      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        {onOpenTool && (
          <>
            <Btn full size="lg" variant="soft" onClick={() => onOpenTool('maint-moment')} leading={<Icon name="sparkle" size={18} />}>
              Moment-to-moment awareness
            </Btn>
            <Btn full size="lg" variant="soft" onClick={() => onOpenTool('maint-daily')} leading={<Icon name="leaf" size={18} />}>
              Daily practices
            </Btn>
            <Btn full size="lg" variant="soft" onClick={() => onOpenTool('maint-monthly')} leading={<Icon name="heart" size={18} />}>
              Monthly check-in{monthlyDue ? ' · Due' : ''}
            </Btn>
          </>
        )}
        <Btn full size="lg" onClick={onRegulate} leading={<Icon name="leaf" size={18} />}>
          Regulate now
        </Btn>
        {onOpenPlan && (
          <Btn full size="lg" variant="soft" onClick={onOpenPlan} leading={<Icon name="book" size={18} />}>
            Self-Love &amp; Maintenance Plan
          </Btn>
        )}
        {onOpenDashboard && (
          <Btn full size="lg" variant="soft" onClick={onOpenDashboard} leading={<Icon name="heart" size={18} />}>
            Your recovery dashboard
          </Btn>
        )}
        {onJourney && (
          <Btn full size="lg" variant="soft" onClick={onJourney} leading={<Icon name="journey" size={18} />}>
            Revisit any chapter
          </Btn>
        )}
        {isCompassion && onRollDice && (
          <Btn full size="lg" variant="soft" onClick={onRollDice} leading={<Icon name="sparkle" size={18} />}>
            Roll the compassion dice
          </Btn>
        )}
      </div>

      <p
        style={{
          margin: '18px auto 0', maxWidth: 330, fontFamily: 'Fraunces, Georgia, serif', fontStyle: 'italic',
          fontSize: 15, lineHeight: 1.5, color: 'var(--ink-soft)',
        }}
      >
        Recovery is never a straight line. Many people return to parts of this journey as life reveals
        the need — that isn’t going backwards, it’s how healing works.
      </p>
    </div>
  );
}
